/**
 * Shared commit signing utilities.
 *
 * This module is the SINGLE SOURCE OF TRUTH for building server-valid
 * signed commits. Both CommitPipeline (workspace writes) and
 * configBlobService (MCP config blobs) import from here.
 *
 * Server validation: CommitSignatureVerifier.cs (ParseCommit + VerifySignature)
 *   - kid = base64url(SHA-256(pk)[0:16])
 *   - sig.val = Ed25519.Sign(SHA-256(canonicalize(unsignedCommit)))
 *   - Commit must contain: v, blobId, parents[], writers[{alg,kid,pk}], payload{}, sig{alg,kid,val}
 */

import * as ed from '@noble/ed25519'
import { canonicalize } from './codec/state'
import type { CryptoProvider } from './types'

// ── Encoding utilities ────────────────────────────────────────────

/** Encode raw bytes as base64url (no pad), per RFC 4648 §5 */
export function base64UrlEncode(bytes: Uint8Array): string {
    // Build binary string via loop — spread (...bytes) crashes on large arrays
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    const str = btoa(binary)
    return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** Decode hex string to Uint8Array */
export function hexToBytes(hex: string): Uint8Array {
    return new Uint8Array(hex.match(/.{2}/g)!.map(b => parseInt(b, 16)))
}

// ── Writer identity ───────────────────────────────────────────────

/**
 * Derive writer kid from an Ed25519 public key.
 * Matches server DeriveKid: base64url(SHA-256(pk)[0:16])
 */
export async function deriveWriterKid(pk: Uint8Array, crypto: CryptoProvider): Promise<string> {
    const hashHex = await crypto.sha256(pk)
    return base64UrlEncode(hexToBytes(hashHex).slice(0, 16))
}

/**
 * Derive writer kid from a writer seed (32-byte Ed25519 private key).
 * Convenience wrapper: getPublicKey(seed) → deriveWriterKid(pk).
 */
export async function deriveWriterKidFromSeed(seed: Uint8Array, crypto: CryptoProvider): Promise<string> {
    const pk = await ed.getPublicKey(seed)
    return deriveWriterKid(pk, crypto)
}

// ── Signed commit builder ─────────────────────────────────────────

export interface WriterEntry {
    alg: 'ed25519'
    kid: string
    pk: string
}

interface SignatureEntry {
    alg: 'ed25519'
    kid: string
    val: string
}

export type CommitPayload = Record<string, unknown> & {
    version?: string
}

export type StateHashCommitPayload = CommitPayload & {
    stateHash: string
}

export interface CommitRecord<TPayload extends CommitPayload = StateHashCommitPayload> {
    v: 1
    blobId: string
    parents: string[]
    time: string // ISO string
    writers: WriterEntry[]
    sig?: SignatureEntry
    payload: TPayload
}

export interface BuildSignedCommitParams {
    blobId: string
    parents: string[]
    stateHash?: string
    payload?: object
    writerSeed: Uint8Array
    crypto: CryptoProvider
    time?: number
    /** Additional writers to include in the commit (§5.4 writer preservation). */
    additionalWriters?: WriterEntry[]
    /** §97: client-managed monotonic version counter. Serialized as decimal string on wire. */
    version?: bigint | number | string
}

function encodeCommitVersion(version: bigint | number | string): string {
    if (typeof version === 'bigint') {
        if (version < 0n) {
            throw new Error('commit payload version must be non-negative')
        }
        return version.toString()
    }

    if (typeof version === 'number') {
        if (!Number.isSafeInteger(version) || version < 0) {
            throw new Error('commit payload version numbers must be non-negative safe integers')
        }
        return version.toString()
    }

    if (!/^(0|[1-9][0-9]*)$/.test(version)) {
        throw new Error('commit payload version strings must be unsigned base-10 integers')
    }

    return version
}

/**
 * Build a server-valid signed commit record.
 *
 * Encapsulates all server-validated logic:
 * 1. Derives pk from seed
 * 2. Derives kid from pk: base64url(SHA-256(pk)[0:16])
 * 3. Builds unsigned CommitRecord
 * 4. Signs SHA-256(canonicalize(unsigned)) with Ed25519
 * 5. Returns canonical bytes + hash
 */
export async function buildSignedCommit(params: BuildSignedCommitParams): Promise<{
    commitBytes: Uint8Array
    commitHash: string
    writerKid: string
}> {
    const {
        blobId,
        parents,
        stateHash,
        payload: payloadInput,
        writerSeed,
        crypto,
        time = Date.now(),
        additionalWriters,
        version,
    } = params

    // 1. Derive writer identity
    const pk = await ed.getPublicKey(writerSeed)
    const pkBase64 = base64UrlEncode(pk)
    const kid = await deriveWriterKid(pk, crypto)

    // 2. Merge writers: current signer + additionalWriters (§5.4 writer preservation)
    const writerMap = new Map<string, WriterEntry>()
    // Include additional writers first (from parent commit)
    if (additionalWriters) {
        for (const w of additionalWriters) writerMap.set(w.kid, w)
    }
    // Current signer always wins (ensures self-authorization)
    writerMap.set(kid, { alg: 'ed25519', kid, pk: pkBase64 })

    // 3. Build unsigned commit
    const payload: CommitPayload = {
        ...((payloadInput ?? {}) as object),
    }
    if (stateHash !== undefined) payload.stateHash = stateHash
    if (version !== undefined) payload.version = encodeCommitVersion(version)
    if (payload.version !== undefined && typeof payload.version !== 'string') {
        payload.version = encodeCommitVersion(payload.version as bigint | number | string)
    }
    if (Object.keys(payload).length === 0) {
        throw new Error('commit payload must not be empty')
    }

    const commitPayload: CommitRecord<CommitPayload> = {
        v: 1,
        blobId,
        parents,
        time: new Date(time).toISOString(), // Serialize as ISO string
        writers: [...writerMap.values()],
        payload,
    }

    // 3. Sign: SHA-256(canonical(unsigned)) → Ed25519
    // Server: toSign = SHA256.HashData(CanonicalizeToUtf8(BuildUnsignedCommitJson))
    const unsignedCanonical = new TextEncoder().encode(canonicalize(commitPayload))
    const toSign = hexToBytes(await crypto.sha256(unsignedCanonical))
    const signature = await crypto.sign(writerSeed, toSign)

    // 4. Add signature
    commitPayload.sig = {
        alg: 'ed25519',
        kid,
        val: base64UrlEncode(signature),
    }

    // 5. Canonical serialize
    const commitBytes = new TextEncoder().encode(canonicalize(commitPayload))
    const commitHash = await crypto.sha256(commitBytes)

    return { commitBytes, commitHash, writerKid: kid }
}

// ── Version extraction (§97) ──────────────────────────────────────

/**
 * Extract the version number from serialized commit bytes.
 * Returns undefined if the commit does not contain a version field.
 *
 * §97: Client-managed monotonic version counter in commit payload.
 */
export function extractVersionFromCommit(commitBytes: Uint8Array): bigint | undefined {
    try {
        const json = new TextDecoder().decode(commitBytes)
        const record = JSON.parse(json) as CommitRecord & {
            payload?: {
                version?: string | number
            }
        }
        const version = record.payload?.version
        if (typeof version === 'string') {
            return /^(0|[1-9][0-9]*)$/.test(version) ? BigInt(version) : undefined
        }
        if (typeof version === 'number') {
            return Number.isSafeInteger(version) && version >= 0 ? BigInt(version) : undefined
        }
        return undefined
    } catch {
        return undefined
    }
}
